import { IconSet } from '@postscript/components';
import {
  BCC,
  CENTRALIZED_OFFERS_VIEW,
  ENTITLEMENTS_BRAND_CENTER,
  NAV_UPDATES_SHOPPER,
  SHOPPER_2_ONBOARDING,
} from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import { useShopperBillingStatus } from 'components/billing/modules/plans/hooks/useShopperBillingStatus';
import { BrandCenterRoutes } from 'components/brand/types';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useMemo } from 'react';
import { NavMenuItem, SubMenuItem } from '../NavMenu';
import { useContentLibraryNewBadge } from './useContentLibraryNewBadge';

const getBrandCenterLink = (url: string) =>
  url.includes('/sources')
    ? BrandCenterRoutes.SOURCES
    : BrandCenterRoutes.VOICE;

export const useBrandCenterMenuItems = () => {
  const shopperStatusContext = useShopperStatus();
  const showContentLibraryNewBadge = useContentLibraryNewBadge();
  const {
    isEligible: isEligibleForInfinityTesting,
    hasProduct: hasInfinityTestingProduct,
  } = useInfinityTestingBillingStatus();
  const { isEligible: isEligibleForShopper } = useShopperBillingStatus();
  const { hasLabsFlag } = usePSLabs();
  const hasNavUpdatesShopper = hasLabsFlag(NAV_UPDATES_SHOPPER);
  const hasBccEnabled = hasLabsFlag(BCC);
  const hasEntitlementsFlag = hasLabsFlag(ENTITLEMENTS_BRAND_CENTER);
  const showStrategyItem = hasLabsFlag(SHOPPER_2_ONBOARDING);

  const showBrandCenterMenuItem =
    hasBccEnabled ||
    isEligibleForInfinityTesting ||
    hasInfinityTestingProduct ||
    isEligibleForShopper ||
    shopperStatusContext.hasShopperFeatureEnabled;

  return useMemo(
    (): NavMenuItem[] =>
      showBrandCenterMenuItem
        ? [
            {
              id: 'brand-center',
              label: 'Brand Center',
              icon: IconSet.SettingsSliders,
              isLoading: shopperStatusContext.isLoading,
              ariaLabelledBy: 'nav-brand-center-title',
              subMenu: [
                {
                  link: () => getBrandCenterLink(window.location.pathname),
                  label: 'Brand Guidelines',
                  id: 'brand-guidelines',
                  entitlementKey: hasEntitlementsFlag
                    ? 'ai::brand_center::view'
                    : undefined,
                },
                ...((showStrategyItem
                  ? [
                      {
                        link: () => BrandCenterRoutes.STRATEGY,
                        label: 'Strategy',
                        id: 'strategy',
                        entitlementKey: hasEntitlementsFlag
                          ? 'ai::brand_center::view'
                          : undefined,
                      },
                    ]
                  : []) as SubMenuItem[]),
                {
                  link: () => BrandCenterRoutes.CONTENT_LIBRARY,
                  label: 'Content Library',
                  id: 'content-library',
                  tag: showContentLibraryNewBadge
                    ? { content: 'New', variant: 'success' as const }
                    : undefined,
                },
                ...(((shopperStatusContext.hasShopperFeatureEnabled ||
                  isEligibleForShopper) &&
                !hasNavUpdatesShopper
                  ? [
                      {
                        link: () => BrandCenterRoutes.SHOPPER,
                        label: 'Shopper',
                        id: 'shopper',
                        entitlementKey: hasEntitlementsFlag
                          ? 'ai::shopper::enroll'
                          : undefined,
                      },
                    ]
                  : []) as SubMenuItem[]),
                {
                  link: BrandCenterRoutes.PRODUCTS,
                  label: 'Product Catalog',
                  id: 'product-catalog',
                },
                {
                  link: BrandCenterRoutes.OFFERS,
                  label: 'Offers',
                  id: 'offers',
                  tag: { content: 'New', variant: 'success' },
                  labsFeatureFlag: CENTRALIZED_OFFERS_VIEW,
                },
              ],
            },
          ]
        : [],
    [
      showBrandCenterMenuItem,
      shopperStatusContext.isLoading,
      shopperStatusContext.hasShopperFeatureEnabled,
      hasEntitlementsFlag,
      hasNavUpdatesShopper,
      isEligibleForShopper,
      showContentLibraryNewBadge,
      showStrategyItem,
    ],
  );
};
